import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, PhoneForwarded, ListTree } from "lucide-react";
import { queryDocs } from "@/integrations/firebase/data";
import { saveMenuNode } from "@/integrations/firebase/admin-ops";
import { MenuNodeEditor } from "@/components/menu-node-editor";
import { PickDealership } from "@/components/pick-dealership";

export const Route = createFileRoute("/_app/routing")({
  head: () => ({ meta: [{ title: "Routing numbers — EESWEB" }] }),
  component: RoutingPage,
});

type NodeRow = {
  id: string; clientId: string; parent_id: string | null; digit: string;
  key_path: string; label: string; node_type: string;
  branch_id: string | null; department_id: string | null; employee_id: string | null;
  workshop_type: string | null; phone_number: string | null; fallback_number: string | null;
  is_active: boolean; sort_order: number;
};

function RoutingPage() {
  const { role, loading, effectiveClientId } = useAuth();
  const qc = useQueryClient();
  const clientId = effectiveClientId;
  const [search, setSearch] = useState("");

  const { data: nodes } = useQuery({
    queryKey: ["routing-nodes", clientId],
    enabled: !!clientId,
    queryFn: async () =>
      ((await queryDocs<NodeRow>("ivr_menu_nodes", { clientId: clientId! })).sort((a, b) => a.key_path.localeCompare(b.key_path))),
  });

  const filtered = useMemo(() => {
    const list = nodes ?? [];
    if (!search.trim()) return list;
    const s = search.toLowerCase();
    return list.filter((n) =>
      [n.label, n.key_path, n.phone_number, n.fallback_number].filter(Boolean).join(" ").toLowerCase().includes(s),
    );
  }, [nodes, search]);

  if (loading) return null;
  if (role !== "admin") return <Navigate to="/dashboard" replace />;

  if (!clientId) return <PickDealership title="Routing numbers" purpose="manage its routing numbers and IVR menu" />;

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Routing numbers</h1>
        <p className="text-sm text-muted-foreground">
          The phone number each IVR key-press option rings, plus a fallback number used when it's
          unavailable. These are used for monitoring and reference — the live routing runs in your
          telephony system.
        </p>
      </div>

      <Tabs defaultValue="builder">
        <TabsList>
          <TabsTrigger value="builder"><ListTree className="size-3.5 mr-1.5" /> Menu builder</TabsTrigger>
          <TabsTrigger value="numbers"><PhoneForwarded className="size-3.5 mr-1.5" /> Numbers table</TabsTrigger>
        </TabsList>

        <TabsContent value="builder">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">IVR menu structure</CardTitle></CardHeader>
            <CardContent>
              <MenuNodeEditor clientId={clientId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="numbers">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base flex items-center gap-2"><PhoneForwarded className="size-4" /> IVR options & numbers</CardTitle>
                <Input className="w-full sm:w-64" placeholder="Search label, key, number…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <tr>
                      <th className="p-3">Key path</th>
                      <th className="p-3">Option</th>
                      <th className="p-3">Routing number</th>
                      <th className="p-3">Fallback number</th>
                      <th className="p-3">Active</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered.map((n) => (
                      <RoutingRow key={n.id} node={n} onSaved={() => qc.invalidateQueries({ queryKey: ["routing-nodes"] })} />
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">
                        {nodes && nodes.length > 0 ? "No options match your search." : "No IVR options configured yet — add them in the Menu builder tab."}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RoutingRow({ node, onSaved }: { node: NodeRow; onSaved: () => void }) {
  const [phone, setPhone] = useState(node.phone_number ?? "");
  const [fallback, setFallback] = useState(node.fallback_number ?? "");
  const [active, setActive] = useState(node.is_active);
  const [saving, setSaving] = useState(false);

  const dirty = phone !== (node.phone_number ?? "") || fallback !== (node.fallback_number ?? "") || active !== node.is_active;

  const save = async () => {
    setSaving(true);
    try {
      await saveMenuNode({
        id: node.id,
        clientId: node.clientId,
        parentId: node.parent_id,
        digit: node.digit,
        label: node.label,
        nodeType: node.node_type as "menu" | "route",
        branchId: node.branch_id,
        departmentId: node.department_id,
        employeeId: node.employee_id,
        workshopType: node.workshop_type,
        phoneNumber: phone.trim() || null,
        fallbackNumber: fallback.trim() || null,
        isActive: active,
        sortOrder: node.sort_order,
      });
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="hover:bg-accent/40">
      <td className="p-3"><Badge variant="outline" className="mono text-xs">{node.key_path}</Badge></td>
      <td className="p-3 font-medium">{node.label}</td>
      <td className="p-3"><Input className="h-8 w-40 mono text-xs" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="—" /></td>
      <td className="p-3"><Input className="h-8 w-40 mono text-xs" value={fallback} onChange={(e) => setFallback(e.target.value)} placeholder="—" /></td>
      <td className="p-3"><Switch checked={active} onCheckedChange={setActive} /></td>
      <td className="p-3">
        <Button size="sm" variant={dirty ? "default" : "ghost"} disabled={!dirty || saving} onClick={save}>
          <Save className="size-3.5 mr-1" /> {saving ? "…" : "Save"}
        </Button>
      </td>
    </tr>
  );
}
